import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import type { ApiQueryParams } from "@/types/api-types";

export const useBookableAssetsQueries = () => {
  const projectId = useProjectId();
  const api = useApi();

  return {
    list: ({ query }: { query: ApiQueryParams<"getBookableAssetsV1"> }) =>
      queryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS(projectId, query),
        queryFn: () => api.getBookableAssetsV1(query),
        select: (data) => commonAPIDataSelector(data).items,
      }),
    listInfinite: ({ query }: { query: ApiQueryParams<"getBookableAssetsV1"> }) => {
      const limit = 12;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_ASSETS_INFINITE(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getBookableAssetsV1({
              ...query,
              Offset: pageParam * limit,
              Limit: limit,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
          if (!lastPage.hasMore) {
            return undefined;
          }

          return pages.length;
        },
      });
    },
    listMyBookingsInfinite: ({ query }: { query: ApiQueryParams<"getBookableAssetsMyBookingsV1"> }) => {
      const limit = 8;

      return infiniteQueryOptions({
        queryKey: QUERY_KEYS.BOOKINGS_USER_RESERVATIONS(projectId, query),
        queryFn: ({ pageParam = 0 }) =>
          api
            .getBookableAssetsMyBookingsV1({
              ...query,
              Offset: pageParam * limit,
              Limit: pageParam === 0 ? limit : undefined,
            })
            .then((items) => commonAPIDataSelector(items)),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
          if (!lastPage.hasMore) {
            return undefined;
          }

          return pages.length;
        },
      });
    },
  };
};
